import React, { useCallback, useEffect, useMemo, useState } from "react";
import { z } from "zod";

import { Modal } from "../../../ui/modal/modal.tsx";
import { Select } from "../../../ui/select/select.tsx";
import { Button } from "../../../ui/button/button.tsx";
import { phase2SendInviteUrl } from "../../../../../config/Endpoints.js";
import { getAuthToken } from "../../../../../utils/Storage.js";
import { ApiError, postEmptyBody } from "../../../../utils/api.ts";
import { showError, showSuccess } from "../../../../utils/toast.ts";
import { useEmployeeNamesListStore } from "../../../../../stores/employeeNamesListStore.js";
import { useSqeLabs } from "../../../../hooks/use-sqe-labs.ts";
import { getApi } from "../../../../utils/api.ts";
import { SQE_PHASE2, sqeRows } from "../../onboarding/sqe/sqe-onboarding-api.ts";

import styles from "./invite-post-join-modal.module.css";

const schema = z.object({
  employeeId: z.string().min(1, "Please select an employee"),
});

export type InvitePostJoinModalProps = {
  open: boolean;
  onClose: () => void;
  onInvited: () => void;
  initialEmployeeId?: string | number;
  lockInitialEmployee?: boolean;
};

function accessToken(): string | undefined {
  const t = getAuthToken("accessToken");
  return t != null && t !== "" ? t : undefined;
}

export function InvitePostJoinModal({
  open,
  onClose,
  onInvited,
  initialEmployeeId,
  lockInitialEmployee = false,
}: InvitePostJoinModalProps) {
  const isSqe = useSqeLabs();
  const [employeeId, setEmployeeId] = useState("");
  const [error, setError] = useState<string | undefined>(undefined);
  const [busy, setBusy] = useState(false);

  const { employeeList, loading: employeesLoading, fetchEmployees } = useEmployeeNamesListStore();
  const [sqeEmployees, setSqeEmployees] = useState<Array<{ value: string; label: string }>>([]);
  const [sqeLoading, setSqeLoading] = useState(false);

  const employeeSelectOptions = useMemo(
    () => isSqe ? sqeEmployees : employeeList.map((o) => ({ value: String(o.value), label: o.label })),
    [employeeList, isSqe, sqeEmployees],
  );

  useEffect(() => {
    if (!open) {
      setEmployeeId("");
      setError(undefined);
      setBusy(false);
      return;
    }
    setEmployeeId(initialEmployeeId != null ? String(initialEmployeeId) : "");
    setError(undefined);
    if (!isSqe && employeeList.length === 0) {
      void fetchEmployees();
    }
    if (isSqe) {
      const controller = new AbortController();
      setSqeLoading(true);
      void (async () => {
        const records: Record<string, unknown>[] = [];
        let page = 0;
        let totalPages = 1;
        do {
          const { data } = await getApi<unknown>(`${SQE_PHASE2}/list?status=PENDING_PREJOINING&currentPage=${page}&pageSize=100`, accessToken(), "json", controller.signal);
          const result = sqeRows(data);
          records.push(...result.rows);
          totalPages = result.totalPages;
          page += 1;
        } while (page < totalPages);
        if (controller.signal.aborted) return;
        setSqeEmployees(records.flatMap((row) => {
          const employee = row.employee && typeof row.employee === "object" ? row.employee as Record<string, unknown> : row;
          const id = employee.id ?? row.employeeId;
          const name = row.employeeName ?? [employee.firstName, employee.lastName].filter(Boolean).join(" ");
          return id == null || !name ? [] : [{ value: String(id), label: String(name) }];
        }));
      })()
        .catch(() => { if (!controller.signal.aborted) showError("Unable to load SQE employees awaiting post-join invitation."); })
        .finally(() => { if (!controller.signal.aborted) setSqeLoading(false); });
      return () => controller.abort();
    }
  }, [open, initialEmployeeId, employeeList.length, fetchEmployees, isSqe]);

  const handleSubmit = useCallback(
    async (e: React.FormEvent) => {
      e.preventDefault();
      const parsed = schema.safeParse({ employeeId: employeeId.trim() });
      if (!parsed.success) {
        setError(parsed.error.issues[0]?.message ?? "Invalid input");
        return;
      }
      setError(undefined);
      const id = Number(parsed.data.employeeId);
      if (!Number.isFinite(id)) {
        setError("Please select a valid employee");
        return;
      }
      setBusy(true);
      try {
        const { data } = await postEmptyBody<{ message?: string }>(
          isSqe ? `${SQE_PHASE2}/send?employeeId=${encodeURIComponent(String(id))}` : phase2SendInviteUrl(id),
          accessToken(),
        );
        const msg =
          data != null && typeof data === "object" && "message" in data && typeof data.message === "string"
            ? data.message
            : "Post-join invite sent.";
        showSuccess(msg);
        onInvited();
        onClose();
      } catch (err) {
        const msg = err instanceof ApiError ? err.message : "Something went wrong. Please try again.";
        showError(msg);
      } finally {
        setBusy(false);
      }
    },
    [employeeId, onClose, onInvited, isSqe],
  );

  return (
    <Modal
      open={open}
      onClose={onClose}
      title="Send Post-Join Invitation"
      size="md"
      footer={
        <div className={styles.footer}>
          <Button type="button" variant="light-grey" onClick={onClose} disabled={busy}>
            Cancel
          </Button>
          <Button type="submit" form="invite-post-join-form" variant="accent" disabled={busy}>
            {busy ? "Sending…" : "Submit"}
          </Button>
        </div>
      }
    >
      <form id="invite-post-join-form" className={styles.form} onSubmit={(e) => void handleSubmit(e)} noValidate>
        <Select
          id="invite-post-employee"
          label="Employee Name"
          required
          searchable
          isClearable={!lockInitialEmployee}
          placeholder="Select Employee Name"
          value={employeeId}
          onChange={(v) => {
            setEmployeeId(v);
            setError(undefined);
          }}
          options={employeeSelectOptions}
          error={error}
          disabled={busy || (isSqe ? sqeLoading : employeesLoading) || lockInitialEmployee}
        />
      </form>
    </Modal>
  );
}
